import { faCheck, faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState } from "react";
import { useHits } from "react-instantsearch";
import { Link } from "react-router-dom";
import { useWorkspaces } from "../state";
import { filterCountLabel, isCapped } from "./chat/filterCount";

const area = (value: unknown) =>
  typeof value === "number" ? `${Math.round(value).toLocaleString("nl-NL")} m²` : null;

// De eerste treffers van het filter, met "+ shortlist". Toevoegen gaat langs
// dezelfde route als "Alle toevoegen": de server haalt naam en geometrie uit de
// cube op dat punt, want een treffer heeft alleen een middelpunt.
export const ExplorationHits = ({
  workspace,
  onShow,
}: {
  workspace: any;
  onShow: (point: [number, number]) => void;
}) => {
  const { items, results } = useHits<any>();
  const fetchWorkspaces = useWorkspaces((s) => s.fetchWorkspaces);
  const [adding, setAdding] = useState<string>();
  const [error, setError] = useState<string>();
  const onShortlist = new Set((workspace.parcels || []).map((p: any) => p.name));

  const add = async (hit: any) => {
    setAdding(hit.objectID);
    setError(undefined);
    try {
      const response = await fetch(`/api/workspaces/${workspace.id}/parcels/bulk`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ points: [hit.geometry] }),
      });
      const body = await response.json();
      if (!response.ok || !body.added?.length) {
        setError(body?.detail || `${hit.name} kon niet worden toegevoegd`);
      }
      fetchWorkspaces(true);
    } finally {
      setAdding(undefined);
    }
  };

  if (!items.length) {
    return <p className="px-2 pb-2 text-xs text-gray-400">Geen treffers voor dit filter</p>;
  }

  const total = results?.nbHits ?? items.length;
  return (
    <>
      <ul className="pb-1">
        {items.map((hit) => (
          <li key={hit.objectID} className="flex items-center gap-2 px-2 py-1 hover:bg-gray-50">
            <button
              onClick={() => onShow(hit.geometry)}
              className="min-w-0 flex-1 text-left"
              title="Toon op de kaart"
            >
              <p className="truncate text-xs font-semibold">{hit.name}</p>
              <p className="truncate text-[11px] text-gray-500">
                {[hit.addresses?.[0], area(hit.area)].filter(Boolean).join(" · ")}
              </p>
            </button>
            {onShortlist.has(hit.name) ? (
              <span className="shrink-0 text-[11px] text-green" title="Staat op de shortlist">
                <FontAwesomeIcon icon={faCheck} />
              </span>
            ) : (
              <button
                onClick={() => add(hit)}
                disabled={adding !== undefined}
                className="shrink-0 rounded bg-bright-yellow px-2 py-0.5 text-[11px] font-bold hover:bg-yellow disabled:opacity-50"
              >
                <FontAwesomeIcon icon={faPlus} className="pr-1" />
                {adding === hit.objectID ? "Bezig…" : "shortlist"}
              </button>
            )}
          </li>
        ))}
      </ul>
      {error && <p className="px-2 pb-1 text-xs text-red-600">{error}</p>}
      {total > items.length && (
        <p className="px-2 pb-2 text-[11px] text-gray-400">
          De eerste {items.length} van {filterCountLabel(total, isCapped(total))}; de rest staat op
          de kaart.
        </p>
      )}
    </>
  );
};

// "leidde tot: …" — de projecten die uit deze verkenning zijn gestart.
export const SpawnedProjects = ({ workspace }: { workspace: any }) => {
  const spawned: { id: number; name: string }[] = workspace.spawned || [];
  if (!spawned.length) return null;
  return (
    <p className="px-2 pb-2 text-xs text-gray-600">
      Leidde tot:{" "}
      {spawned.map((project, index) => (
        <span key={project.id}>
          {index > 0 && ", "}
          <Link to={`/workspace/${project.id}`} className="text-green hover:underline">
            {project.name}
          </Link>
        </span>
      ))}
    </p>
  );
};
